import React from "react";
import "~/less/web-internal.less";
import { cls } from "cls";
import connectUserAndChat from "~/common/Store/connectUserAndChat";
import { User } from "~/common/Store/models/User";
import useKeymap from "~/shared/hooks/useKeymap";
import useLoggedInAs from "~/common/hooks/useLoggedInAs";
import { useRecentInternalNotesUpdate } from "./hooks/useRecentInternalNotesUpdate";

interface Props {
  showInternalNotesModal: boolean;
  toggleInternalNotesModal: () => void;
  user: User;
  itemNumber: string;
}

const InternalNotesButton = connectUserAndChat(
  ({
    showInternalNotesModal,
    toggleInternalNotesModal,
    user,
    itemNumber,
  }: Props) => {
    const showLoggedInAsBanner = useLoggedInAs(user);

    useKeymap({
      "alt+`": toggleInternalNotesModal,
    });

    const isInternal =
      user.analyticsData?.is_on_vpn?.toLocaleUpperCase() === "YES";

    const loggedInAsBannerStyles = cls(
      "bottom-[5.5rem]",
      "sm:bottom-[4.5rem]",
      "lg:bottom-2.5",
    );

    const internalNotesRecentUpdate = useRecentInternalNotesUpdate(
      itemNumber,
      isInternal,
    );

    const dateModifiedFormatted = internalNotesRecentUpdate.dateModified?.length
      ? new Date(internalNotesRecentUpdate.dateModified).toLocaleDateString(
          "en-US",
          {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          },
        )
      : "";

    return internalNotesRecentUpdate.dateModified?.length > 0 ||
      internalNotesRecentUpdate.userName?.length > 0 ? (
      <button
        onClick={toggleInternalNotesModal}
        id="web-internal-trigger"
        className={cls(
          "unstyled-button", //// only resets visuals
          "fixed bottom-2.5 right-2.5 z-1000 max-w-[134px]",
          "group flex items-center px-[7px] py-[7px] text-sm-1/2 font-normal",
          "rounded [box-shadow:0_1px_4px_rgba(0,0,0,0.3)] cursor-pointer leading-none",

          !showInternalNotesModal
            ? "bg-white text-[#686b66]"
            : "bg-[#188d00] text-[#eaf7e0] antialiased",
          showLoggedInAsBanner && loggedInAsBannerStyles,
        )}
        data-track="internal-notes-click"
        data-track-ei="internal-notes-pdp"
        data-testid="show-internal-notes-button"
      >
        <i
          className={cls(
            "icon-attention mr-[7px] text-[21px]",
            showInternalNotesModal ? "!text-white" : "text-red-500",
          )}
        />
        <span className="webinfo_heading flex flex-col items-start text-left text-sm-1/2">
          <span
            className={cls(
              "whitespace-nowrap text-inherit",
              !showInternalNotesModal && "group-hover:text-[#333]",
            )}
          >
            Internal Notes
          </span>

          {(internalNotesRecentUpdate.userName?.length > 0 ||
            internalNotesRecentUpdate.dateModified?.length > 0) && (
            <span
              className={cls(
                "block",
                showInternalNotesModal
                  ? "text-inherit antialiased"
                  : "text-[#333]",
              )}
            >
              {dateModifiedFormatted?.length > 0 && (
                <strong
                  className={cls(showInternalNotesModal && "text-white")}
                  data-testid="internal-notes-date-updated"
                >{`${dateModifiedFormatted} `}</strong>
              )}
              {internalNotesRecentUpdate.userName?.length > 0 && (
                <>
                  {"by "}
                  <strong
                    className={cls(showInternalNotesModal && "text-white")}
                    data-testid="internal-notes-user-updated"
                  >
                    {internalNotesRecentUpdate.userName}
                  </strong>
                </>
              )}
            </span>
          )}
        </span>
      </button>
    ) : null;
  },
);

export default InternalNotesButton;
